import {
  Button,
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
} from '@commenda/kakashi';
import { useState } from 'react';

import { useReport } from '../context/ReportContext';
import { ReportComponentHelper } from '../helpers/ReportComponentHelper';
import type { ReportComponentId, ReportComponentKind } from '../types/report';

const KIND_META: Record<ReportComponentKind, { label: string }> = {
  column_header: { label: 'Headlines' },
  tile: { label: 'Tiles' },
  chart: { label: 'Charts' },
  breakdown_list: { label: 'Lists' },
  table: { label: 'Tables' },
};

const KINDS: ReportComponentKind[] = [
  'column_header',
  'tile',
  'chart',
  'breakdown_list',
  'table',
];

/**
 * Settings-button dropdown listing every report component with an on/off
 * toggle. Turning one on appends it to the canvas; off removes every instance.
 */
export const ComponentsMenu = () => {
  const { items, setItems, columnLayout } = useReport();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inUse = new Set(items.map((i) => i.componentId));
  const needle = query.trim().toLowerCase();
  const visible = ReportComponentHelper.palette(columnLayout).filter((d) =>
    d.title.toLowerCase().includes(needle)
  );
  const visibleKinds = KINDS.filter((kind) =>
    visible.some((d) => d.kind === kind)
  );

  const handleToggle = (id: ReportComponentId, checked: boolean) =>
    setItems((prev) =>
      checked
        ? [
            ...prev,
            ReportComponentHelper.newItem(id, {
              x: 0,
              y: ReportComponentHelper.nextRow(prev),
            }),
          ]
        : prev.filter((i) => i.componentId !== id)
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          aria-pressed={open}
          leftIcon={<Icons.Settings01 className="size-4" />}
        >
          Configure report
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={4}
        avoidCollisions={false}
        // Figma "Dropdown / Chat history": its shadow is softer than any shared elevation token.
        className="flex max-h-[425px] w-[339px] flex-col overflow-hidden rounded-xl border-1/2 border-border-secondary p-none shadow-[0px_2px_8px_rgba(0,0,0,0.04)]"
      >
        <div className="shrink-0 px-lg">
          <input
            type="text"
            autoComplete="off"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search components"
            aria-label="Search components"
            className="w-full appearance-none border-0 bg-transparent py-md txt-small-regular text-text-primary placeholder:text-text-placeholder focus:outline-none focus:ring-0"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto py-sm">
          {visible.length === 0 && (
            <p className="px-lg py-md txt-small-regular text-text-placeholder">
              No components match your search.
            </p>
          )}
          {KINDS.map((kind) => {
            const definitions = visible.filter((d) => d.kind === kind);
            if (definitions.length === 0) return null;
            return (
              <div key={kind}>
                {kind !== visibleKinds[0] && (
                  <div className="py-sm">
                    <div className="h-[0.5px] bg-border-secondary" />
                  </div>
                )}
                <p className="px-lg py-md txt-extra-small-medium text-text-secondary">
                  {KIND_META[kind].label}
                </p>
                <ul className="px-sm">
                  {definitions.map((definition) => {
                    const switchId = `report-component-${definition.id}`;
                    return (
                      <li key={definition.id}>
                        <label
                          htmlFor={switchId}
                          className="flex w-full cursor-pointer items-center gap-sm rounded-sm py-md pl-md pr-lg transition-colors hover:bg-bg-secondary"
                        >
                          <span className="min-w-0 flex-1 truncate txt-small-medium text-text-primary">
                            {definition.title}
                          </span>
                          <Switch
                            id={switchId}
                            checked={inUse.has(definition.id)}
                            onCheckedChange={(checked) =>
                              handleToggle(definition.id, checked)
                            }
                          />
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
